<script lang="ts">
import { computed, defineComponent, h, mergeProps, ref, unref } from 'vue';

import { classNames, getUid } from '@components/utils';

import {
  makeDelayProps,
  makeDimensionProps,
  makeLazyProps,
  makeOverlayProps,
  makeTextProps,
  makeThemeProps,
  makeTransitionProps,
  provideTheme,
  useProxyModel,
  useRender,
  useScopeId
} from '../../composable';
import { CdsOverlay } from '../CdsOverlay';
import { makeActivatorProps, makeLocationStrategyProps, makeScrollStrategyProps } from '../CdsOverlay/composable';

import type { ILocationStrategyProps } from '../CdsOverlay/composable';
import type { PropType } from 'vue';

export const componentName = 'CdsTooltip';
export default defineComponent({
  name: componentName,
  props: {
    /**
     * Уникальный идентификатор
     */
    id: {
      type: String as PropType<string>,
      default: undefined
    },
    /**
     * Убрать указатель направления
     */
    hideArrow: {
      type: Boolean as PropType<boolean>,
      default: false
    },
    ...makeTextProps(),
    ...makeThemeProps(),
    ...makeOverlayProps({
      scrim: false,
      closeOnBack: false
    }),
    ...makeActivatorProps({
      openOnClick: false,
      openOnHover: true
    }),
    ...makeLocationStrategyProps({
      origin: 'auto' as const,
      locationStrategy: 'connected' as const,
      location: 'top' as const,
      offset: 8
    }),
    ...makeScrollStrategyProps({
      scrollStrategy: 'none' as const
    }),
    ...makeLazyProps({
      eager: true
    }),
    ...makeDimensionProps({
      minWidth: 0
    }),
    ...makeTransitionProps({
      transition: false
    }),
    ...makeDelayProps()
  },
  emits: {
    /**
     * Событие при изменении значения (v-model)
     * @param value
     */
    'update:modelValue': (value: boolean) => true
  },
  setup(props, { slots, expose }) {
    const isActive = useProxyModel(props, 'modelValue');
    const { themeClasses } = provideTheme(props);
    const { scopeId } = useScopeId();

    const uid = getUid();
    const id = computed(() => props.id || `cds-tooltip-${uid}`);

    const overlay = ref<typeof CdsOverlay>();

    const location = computed(() =>
      props.location.split(' ').length > 1
        ? props.location
        : ((props.location + ' center') as ILocationStrategyProps['location'])
    );
    const origin = computed(() =>
      props.origin === 'auto' ||
      props.origin === 'overlap' ||
      props.origin.split(' ').length > 1 ||
      props.location.split(' ').length > 1
        ? props.origin
        : ((props.origin + ' center') as ILocationStrategyProps['origin'])
    );
    const transition = computed(() => {
      if (props.transition) return props.transition;
      return isActive.value ? 'scale-transition' : 'fade-transition';
    });

    const activatorProps = computed(() =>
      mergeProps(
        {
          'aria-describedby': unref(id)
        },
        props.activatorProps
      )
    );

    useRender(() =>
      h(
        CdsOverlay,
        {
          ref: overlay,
          ...props,
          id: unref(id),
          absolute: true,
          persistent: true,
          modelValue: unref(isActive),
          location: unref(location),
          origin: unref(origin),
          activatorProps: unref(activatorProps),
          transition: unref(transition),
          class: classNames(
            'cds-tooltip',
            { 'cds-tooltip--hide-arrow': props.hideArrow, [unref(location)]: true },
            unref(themeClasses)
          ),
          role: 'tooltip',
          _disableGlobalStack: true,
          ...scopeId
        },
        {
          activator: slots.activator,
          default: (...args: any) => slots.default?.(...args) ?? props.text
        }
      )
    );

    expose({
      id,
      overlay
    });

    return {};
  }
});
</script>

<style lang="scss">
@import './CdsTooltip';
</style>
